import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelectedColoc } from '../hooks/useSelectedColoc';
import { candidatsService } from '../services/candidats.service';
import { roomsService } from '../services/rooms.service';
import { authService } from '../services/auth.service';
import { RoomDetail } from '../types/room';
import {
  CandidateDetail,
  CandidateFile,
  AcceptCandidateRequest,
  getCandidateStatusLabel,
  getCandidateStatusColor,
  PROFESSIONAL_STATUS_LABELS,
  GENDER_LABELS,
} from '../types/candidat';
import LeasePanel from '../components/LeasePanel';
import { formatDate } from '../utils/format';
import { getErrorMessage } from '../utils/error';
import { urlChambrePublique } from '../utils/publicUrl';
import '../styles/CandidatsPage.css';

/**
 * Le dossier d'un candidat — une PAGE, plus un panneau latéral.
 *
 * Ce détail fait près de deux cents lignes : chambre, état civil, garant,
 * pièces, bail. Il a d'abord vécu dans une ligne dépliée au milieu du tableau,
 * puis dans un panneau de 460 px. Les deux écrasaient le même contenu ; le
 * panneau masquait en plus la moitié des colonnes de la liste pour tenir.
 *
 * Une page apporte ce qu'aucun repli n'aura : la largeur, une URL partageable,
 * et un vrai retour.
 *
 * **Les décisions vivent ici**, pas dans la liste : accepter ou rejeter engage
 * un bail ou détruit des pièces, et cela se fait dossier ouvert — c'est le même
 * choix que sur l'écran Agences, où la liste liste et la fiche agit.
 */
const CandidatDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();
  const isAgency = authService.isAgency();
  const listeUrl = isAgency ? '/agency/candidats' : '/candidats';

  const [candidate, setCandidate] = useState<CandidateDetail | null>(null);
  // Les autres candidats de la coloc : ils servent à savoir si la chambre a
  // déjà un ACCEPTED, seule condition qui autorise à réactiver un STANDBY.
  const [freres, setFreres] = useState<CandidateDetail[]>([]);
  const [rooms, setRooms] = useState<RoomDetail[]>([]);
  const [introuvable, setIntrouvable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [files, setFiles] = useState<CandidateFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [apercu, setApercu] = useState<
    { url: string | null; nom: string; chargement: boolean } | null
  >(null);

  // Modale d'acceptation
  const [acceptOuvert, setAcceptOuvert] = useState(false);
  const [acceptRoom, setAcceptRoom] = useState<RoomDetail | null>(null);
  const [acceptLeaseStart, setAcceptLeaseStart] = useState('');
  const [acceptRent, setAcceptRent] = useState('');
  const [acceptCharges, setAcceptCharges] = useState('');
  const [acceptDeposit, setAcceptDeposit] = useState('');
  const [acceptAgencyExpenses, setAcceptAgencyExpenses] = useState('');
  const [acceptEmail, setAcceptEmail] = useState('');
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // Modale de rejet
  const [rejectOuvert, setRejectOuvert] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  // Toujours décoché à l'ouverture : purger d'anciennes candidatures ne doit
  // jamais envoyer d'emails par inadvertance.
  const [rejectSendEmail, setRejectSendEmail] = useState(false);

  const loadData = useCallback(async () => {
    // Pas encore de colocation sélectionnée : on RESTE en chargement. Sur un
    // lien direct, `Layout` la pose juste après le montage et l'évènement
    // `colocChanged` relance ce chargement — rendre « introuvable » entre les
    // deux ferait clignoter un message faux.
    if (!selectedColoc?.id || !id) return;
    setLoading(true);
    try {
      // Pas d'endpoint « un candidat » : la liste de la coloc est déjà bornée
      // par le backend et reste courte. Même choix que la page de dépôt agence.
      const liste = await candidatsService.listForColoc(selectedColoc.id);
      const trouve = liste.find((c) => c.id === id) ?? null;
      setFreres(liste);
      setCandidate(trouve);
      setIntrouvable(trouve === null);
      // L'endpoint chambres est owner-only : l'agence n'y a pas accès, et n'en
      // a pas besoin — elle ne prépare pas de bail.
      if (!isAgency) setRooms(await roomsService.listForColoc(selectedColoc.id));
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement du candidat'));
    } finally {
      setLoading(false);
    }
  }, [selectedColoc, id, isAgency]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!id) return;
    setFilesLoading(true);
    candidatsService
      .getFiles(id)
      .then(setFiles)
      .catch(() => setFiles([]))
      .finally(() => setFilesLoading(false));
  }, [id]);

  /**
   * L'API sert les pièces en `application/octet-stream` pour forcer le
   * téléchargement — un `iframe` ne sait alors rien en faire et reste blanc. On
   * rétablit le type d'après l'extension, qui est fiable : le formulaire de
   * candidature n'accepte que ces quatre-là.
   */
  const retyper = (blob: Blob, nom: string): Blob => {
    const types: Record<string, string> = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
    };
    const type = types[nom.split('.').pop()?.toLowerCase() ?? ''];
    return type ? new Blob([blob], { type }) : blob;
  };

  /**
   * Aperçu en ligne d'une pièce. Évaluer un dossier demandait de télécharger
   * huit fichiers puis de les ouvrir hors du produit — sur l'écran qui porte
   * justement la décision d'accepter ou non.
   *
   * L'URL est un blob local, révoqué à la fermeture : le fichier n'est jamais
   * exposé par une URL devinable, et rien ne subsiste après consultation.
   */
  const ouvrirApercu = async (fileId: string, filename: string) => {
    if (!id) return;
    setApercu({ url: null, nom: filename, chargement: true });
    try {
      const blob = await candidatsService.downloadFile(id, fileId);
      setApercu({
        url: URL.createObjectURL(retyper(blob, filename)),
        nom: filename,
        chargement: false,
      });
    } catch {
      setApercu(null);
      alert('Aperçu impossible pour ce document.');
    }
  };

  const fermerApercu = () => {
    if (apercu?.url) URL.revokeObjectURL(apercu.url);
    setApercu(null);
  };

  const handleDownloadFile = async (fileId: string, filename: string) => {
    if (!id) return;
    try {
      const blob = await candidatsService.downloadFile(id, fileId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Erreur lors du téléchargement du fichier.');
    }
  };

  const handleDownloadArchive = async () => {
    if (!candidate) return;
    setArchiveLoading(true);
    try {
      const blob = await candidatsService.downloadArchive(candidate.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `candidature-${candidate.lastName}-${candidate.firstName}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("Erreur lors du téléchargement de l'archive.");
    } finally {
      setArchiveLoading(false);
    }
  };

  const openAcceptModal = () => {
    if (!candidate) return;
    const room = rooms.find((r) => r.id === candidate.roomId);
    setAcceptRoom(room || null);
    setAcceptEmail(candidate.email);
    setAcceptLeaseStart('');
    setAcceptRent(room?.priceNIC?.toString() || '');
    setAcceptCharges(room?.charges?.toString() || '');
    setAcceptDeposit(room?.depositAmount?.toString() || '');
    setAcceptAgencyExpenses(room?.agencyExpenses?.toString() || '');
    setAcceptError(null);
    setAcceptOuvert(true);
  };

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidate || !acceptRoom) return;

    setAcceptLoading(true);
    setAcceptError(null);
    try {
      const data: AcceptCandidateRequest = {
        room_id: acceptRoom.id,
        first_name: candidate.firstName,
        last_name: candidate.lastName,
        email: acceptEmail,
        phone_number: candidate.phoneNumber || undefined,
        gender: candidate.gender || undefined,
        date_of_birth: candidate.dateOfBirth || undefined,
        school: candidate.school || undefined,
        lease_start_date: acceptLeaseStart,
        monthly_rent: parseFloat(acceptRent),
        charges_provision: parseFloat(acceptCharges),
        deposit_amount: acceptDeposit ? parseFloat(acceptDeposit) : undefined,
        agency_expenses: acceptAgencyExpenses
          ? parseFloat(acceptAgencyExpenses)
          : undefined,
      };
      await candidatsService.accept(candidate.id, data);
      setAcceptOuvert(false);
      loadData();
    } catch (err) {
      setAcceptError(getErrorMessage(err, "Erreur lors de l'acceptation"));
    } finally {
      setAcceptLoading(false);
    }
  };

  const openRejectModal = () => {
    setRejectError(null);
    // Toujours décoché à l'ouverture, cf. déclaration de l'état.
    setRejectSendEmail(false);
    setRejectOuvert(true);
  };

  const handleReject = async () => {
    if (!candidate) return;
    setRejectLoading(true);
    setRejectError(null);
    try {
      await candidatsService.reject(candidate.id, rejectSendEmail);
      setRejectOuvert(false);
      loadData();
    } catch (err) {
      setRejectError(getErrorMessage(err, 'Erreur lors du rejet'));
    } finally {
      setRejectLoading(false);
    }
  };

  // Après une suppression, le dossier n'existe plus : rester sur sa page
  // afficherait « Candidature introuvable ». On rend la main à la liste.
  const handleDelete = async () => {
    if (!candidate) return;
    if (
      !window.confirm(
        'Supprimer définitivement cette candidature et toutes ses pièces ? '
          + 'Cette action est irréversible et aucun email ne sera envoyé.',
      )
    ) return;
    setError(null);
    try {
      await candidatsService.remove(candidate.id);
      navigate(listeUrl);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la suppression'));
    }
  };

  const handleReopen = async () => {
    if (!candidate) return;
    try {
      await candidatsService.reopen(candidate.id);
      loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la réouverture'));
    }
  };

  /**
   * Un STANDBY ne peut repasser en PENDING que si sa chambre n'a plus de
   * candidat ACCEPTED — sans ce garde-fou, deux candidats se retrouveraient
   * ACCEPTED sur la même chambre.
   */
  const peutReactiver =
    candidate?.status === 'STANDBY'
    && !!candidate.roomId
    && !freres.some(
      (c) => c.roomId === candidate.roomId && c.status === 'ACCEPTED',
    );

  if (loading) return <div className="loading">Chargement...</div>;

  if (introuvable || !candidate) {
    return (
      <div className="candidats-page">
        <div className="socle-vide">
          <p className="socle-vide-titre">Candidature introuvable</p>
          <p className="socle-vide-aide">
            Elle a été supprimée, ou elle appartient à une autre colocation que
            celle sélectionnée.
          </p>
          <button className="btn-action btn-secondary" onClick={() => navigate(listeUrl)}>
            Retour aux candidats
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="candidats-page">
      <div className="candidats-header">
        <div>
          <button
            className="btn-action btn-secondary"
            style={{ marginBottom: 8 }}
            onClick={() => navigate(listeUrl)}
          >
            ← Candidats
          </button>
          <h2>
            {candidate.firstName} {candidate.lastName}
            <span
              className="candidat-status-badge"
              style={{
                backgroundColor: getCandidateStatusColor(candidate.status),
                marginLeft: 12,
                verticalAlign: 'middle',
              }}
            >
              {getCandidateStatusLabel(candidate.status)}
            </span>
          </h2>
          <p className="access-hint" style={{ margin: 0 }}>
            {candidate.roomName || 'Chambre non précisée'}
          </p>
        </div>

        {/* Les décisions sont ici, dossier ouvert — la liste ne fait que lister. */}
        {!isAgency && (
          <div className="cx-actions">
            {candidate.status === 'PENDING' && (
              <button className="btn-action btn-save" onClick={openAcceptModal}>
                Accepter
              </button>
            )}
            {peutReactiver && (
              <button
                className="btn-action btn-save"
                onClick={openAcceptModal}
                title="La chambre est libre, vous pouvez relancer ce candidat"
              >
                Réactiver
              </button>
            )}
            {(candidate.status === 'PENDING' || candidate.status === 'STANDBY') && (
              <button className="btn-action btn-danger" onClick={openRejectModal}>
                Rejeter
              </button>
            )}
            {candidate.status === 'REJECTED' && (
              <>
                <button
                  className="btn-action btn-save"
                  onClick={handleReopen}
                  title="Rouvrir le dossier — n'est possible que si le bail a été annulé (les pièces sont encore disponibles)"
                >
                  Rouvrir le dossier
                </button>
                <button
                  className="btn-action btn-danger"
                  onClick={handleDelete}
                  title="Effacer définitivement la candidature et ses pièces, sans envoyer d'email"
                >
                  Supprimer
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {error && <div className="candidats-error">{error}</div>}

      <div className="candidat-detail-grid">
        <div className="detail-section">
          <h4>Coordonnées</h4>
          <div className="detail-item">
            <span>Email</span>
            <span><a href={`mailto:${candidate.email}`}>{candidate.email}</a></span>
          </div>
          <div className="detail-item">
            <span>Téléphone</span>
            <span>
              {candidate.phoneNumber
                ? <a href={`tel:${candidate.phoneNumber}`}>{candidate.phoneNumber}</a>
                : '—'}
            </span>
          </div>
        </div>

        <div className="detail-section">
          <h4>Chambre</h4>
          <div className="detail-item">
            <span>Nom</span>
            <span>{candidate.roomName || '—'}</span>
          </div>
          <div className="detail-item">
            <span>Numéro</span>
            <span>{candidate.roomNumber ?? '—'}</span>
          </div>
          {candidate.colocSlug && candidate.roomSlug && (
            <div className="detail-item">
              <span>Page publique</span>
              <a
                href={urlChambrePublique(candidate.colocSlug, candidate.roomSlug)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Voir la chambre ↗
              </a>
            </div>
          )}
        </div>

        <div className="detail-section">
          <h4>Informations personnelles</h4>
          <div className="detail-item">
            <span>Civilité</span>
            <span>{candidate.gender === 'Mr' ? 'M.' : (candidate.gender || '—')}</span>
          </div>
          <div className="detail-item">
            <span>Prénom</span>
            <span>{candidate.firstName || '—'}</span>
          </div>
          <div className="detail-item">
            <span>Nom</span>
            <span>{candidate.lastName || '—'}</span>
          </div>
          <div className="detail-item">
            <span>Date de naissance</span>
            <span>{candidate.dateOfBirth ? formatDate(candidate.dateOfBirth) : '—'}</span>
          </div>
          <div className="detail-item">
            <span>Statut professionnel</span>
            <span>{candidate.professionalStatus ? (PROFESSIONAL_STATUS_LABELS[candidate.professionalStatus] || candidate.professionalStatus) : '—'}</span>
          </div>
          <div className="detail-item">
            <span>École / Entreprise</span>
            <span>{candidate.school || '—'}</span>
          </div>
          {candidate.studiesEndYear != null && (
            <div className="detail-item">
              <span>Fin d&apos;études prévue</span>
              <span>
                {candidate.studiesEndYear}
                {(() => {
                  const yearsLeft = candidate.studiesEndYear! - new Date().getFullYear();
                  return yearsLeft > 0 ? ` (~${yearsLeft} an${yearsLeft > 1 ? 's' : ''})` : '';
                })()}
              </span>
            </div>
          )}
        </div>

        {candidate.guarantorType === 'INSTITUTIONAL' && (
          <div className="detail-section">
            <h4>Garantie institutionnelle</h4>
            <div className="detail-item">
              <span>Organisme</span>
              <span>{candidate.guarantorSchemeCode || '—'}</span>
            </div>
          </div>
        )}

        {candidate.guarantorType === 'INDIVIDUAL' && (
          <div className="detail-section">
            <h4>Garant</h4>
            <div className="detail-item">
              <span>Nom</span>
              <span>{candidate.guarantorFirstName} {candidate.guarantorLastName}</span>
            </div>
            <div className="detail-item">
              <span>Email</span>
              <span>{candidate.guarantorEmail || '—'}</span>
            </div>
            <div className="detail-item">
              <span>Téléphone</span>
              <span>{candidate.guarantorPhoneNumber || '—'}</span>
            </div>
            <div className="detail-item">
              <span>Genre</span>
              <span>{candidate.guarantorGender ? (GENDER_LABELS[candidate.guarantorGender] || candidate.guarantorGender) : '—'}</span>
            </div>
            <div className="detail-item">
              <span>Date de naissance</span>
              <span>{candidate.guarantorDateOfBirth ? formatDate(candidate.guarantorDateOfBirth) : '—'}</span>
            </div>
            <div className="detail-item">
              <span>Statut professionnel</span>
              <span>{candidate.guarantorProfessionalStatus ? (PROFESSIONAL_STATUS_LABELS[candidate.guarantorProfessionalStatus] || candidate.guarantorProfessionalStatus) : '—'}</span>
            </div>
            <div className="detail-item">
              <span>École / Entreprise</span>
              <span>{candidate.guarantorSchool || '—'}</span>
            </div>
          </div>
        )}

        {candidate.comment && (
          <div className="detail-section">
            <h4>Message</h4>
            <p className="candidat-comment">{candidate.comment}</p>
          </div>
        )}

        <div className="detail-section">
          <div className="detail-section-header">
            <h4>Documents</h4>
            {files.length > 1 && (
              <button
                className="btn-action btn-download"
                onClick={handleDownloadArchive}
                disabled={archiveLoading}
              >
                {archiveLoading
                  ? 'Préparation...'
                  : `Tout télécharger (${files.length})`}
              </button>
            )}
          </div>
          {filesLoading ? (
            <p>Chargement des fichiers...</p>
          ) : files.length === 0 ? (
            <p className="no-files">
              {candidate.status === 'CONFIRMED'
                ? 'Les pièces ont rejoint le dossier du locataire, dans Locataires.'
                : 'Aucun document fourni.'}
            </p>
          ) : (
            <div className="files-list">
              {files.map((file) => {
                const filename = file.filepath.includes('/')
                  ? file.filepath.split('/').pop()!
                  : file.filepath;
                return (
                  <div key={file.id} className="file-item">
                    <span className="file-name">
                      {file.isGuarantorFile && <span className="guarantor-file-badge">Garant</span>}
                      {filename}
                    </span>
                    <span className="file-actions">
                      <button
                        className="btn-action btn-preview"
                        onClick={() => ouvrirApercu(file.id, filename)}
                      >
                        Voir
                      </button>
                      <button
                        className="btn-action btn-download"
                        onClick={() => handleDownloadFile(file.id, filename)}
                      >
                        Télécharger
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Le bail : le composant se tait de lui-même pour les candidatures qui
          n'en ont pas encore (PENDING / STANDBY / REJECTED). */}
      {!isAgency && (candidate.status === 'ACCEPTED' || candidate.status === 'CONFIRMED') && (
        <LeasePanel candidateId={candidate.id} onLeaseChange={loadData} />
      )}

      {acceptOuvert && (
        <div className="modal-overlay" onClick={() => setAcceptOuvert(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <h3>Accepter la candidature</h3>
            <p className="modal-subtitle">
              {candidate.firstName} {candidate.lastName} — {candidate.roomName || 'Chambre'}
            </p>
            {acceptError && <div className="candidats-error">{acceptError}</div>}

            <form onSubmit={handleAccept} className="candidats-form">
              <div className="form-section-title">Informations du bail</div>
              <div className="form-row">
                <div className="form-group">
                  <label>Date de début de bail *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={acceptLeaseStart}
                    onChange={e => setAcceptLeaseStart(e.target.value)}
                    required
                  />
                  {acceptRoom?.noticeExpectedExitDate && (
                    <small className="form-hint">
                      Fin de préavis actuel : {formatDate(acceptRoom.noticeExpectedExitDate)}
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Loyer HC (€) *</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={acceptRent}
                    onChange={e => setAcceptRent(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Provision charges (€) *</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={acceptCharges}
                    onChange={e => setAcceptCharges(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Dépôt de garantie (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={acceptDeposit}
                    onChange={e => setAcceptDeposit(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Frais d&apos;agence (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={acceptAgencyExpenses}
                    onChange={e => setAcceptAgencyExpenses(e.target.value)}
                  />
                </div>
              </div>

              <div className="form-section-title">Compte locataire</div>
              <p className="form-hint-block">
                Adresse du compte locataire, <strong>créé dès l&apos;acceptation</strong>
                pour réserver la chambre. Le locataire n&apos;y accède qu&apos;au
                début de son bail, et choisit lui-même son mot de passe via un
                lien qui lui est envoyé.
              </p>
              <div className="form-row">
                <div className="form-group">
                  <label>Email du locataire *</label>
                  <input
                    type="email"
                    className="form-control"
                    value={acceptEmail}
                    onChange={e => setAcceptEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-action btn-secondary"
                  onClick={() => setAcceptOuvert(false)}
                >
                  Annuler
                </button>
                <button type="submit" className="btn-action btn-save" disabled={acceptLoading}>
                  {acceptLoading ? 'Acceptation en cours...' : 'Accepter et préparer le bail'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modale de rejet — l'action est irréversible (les pièces sont détruites)
          et peut notifier le candidat : elle mérite sa confirmation. */}
      {rejectOuvert && (
        <div className="modal-overlay" onClick={() => setRejectOuvert(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Rejeter la candidature</h3>
            <p className="modal-subtitle">
              {candidate.firstName} {candidate.lastName} — {candidate.roomName || 'Chambre'}
            </p>
            {rejectError && <div className="candidats-error">{rejectError}</div>}

            <p className="reject-warning">
              Les pièces jointes de ce dossier seront supprimées définitivement,
              que le candidat soit prévenu ou non.
            </p>

            <label className="reject-notify">
              <input
                type="checkbox"
                checked={rejectSendEmail}
                onChange={e => setRejectSendEmail(e.target.checked)}
              />
              Prévenir le candidat par email
            </label>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-action btn-secondary"
                onClick={() => setRejectOuvert(false)}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn-action btn-danger"
                onClick={handleReject}
                disabled={rejectLoading}
              >
                {rejectLoading ? 'Rejet en cours...' : 'Rejeter la candidature'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visionneuse de pièce. `<iframe>` et non `<img>` : les candidats
          envoient des PDF autant que des photos, et le navigateur choisit seul
          le bon rendu. Le fichier reste un blob local. */}
      {apercu && (
        <div className="apercu-fond" onClick={fermerApercu} role="dialog" aria-modal="true">
          <div className="apercu-cadre" onClick={(e) => e.stopPropagation()}>
            <div className="apercu-entete">
              <span className="apercu-nom">{apercu.nom}</span>
              <button className="apercu-fermer" onClick={fermerApercu} aria-label="Fermer">
                ✕
              </button>
            </div>
            {apercu.chargement || !apercu.url ? (
              <p className="apercu-attente">Chargement du document…</p>
            ) : (
              <iframe className="apercu-contenu" src={apercu.url} title={apercu.nom} />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidatDetailPage;
